import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where
} from 'firebase/firestore';
import { auth, db } from '../firebase';
import {
  CLOUD_COLLECTIONS,
  CLOUD_FEATURE_FLAGS,
  createCampaignGrindId
} from './cloudSchema';

function assertCloudGrindsEnabled() {
  if (!CLOUD_FEATURE_FLAGS.cloudGrindsEnabled) {
    throw new Error('Cloud Grinds are disabled for this local stage.');
  }
}

function getAuthenticatedViewerId() {
  const viewerId = auth.currentUser?.uid;
  if (!viewerId) {
    throw new Error('Sign in to Grind this campaign.');
  }
  return viewerId;
}

function getRequiredIdPart(value, fieldName) {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${fieldName} is required.`);
  }
  if (value.includes('/')) {
    throw new Error(`${fieldName} cannot contain path separators.`);
  }
  return value.trim();
}

export function toCloudGrindRecord({ campaign, viewerId }) {
  const campaignId = getRequiredIdPart(campaign?.id, 'campaignId');
  const creatorId = getRequiredIdPart(campaign?.creatorId, 'creatorId');
  const safeViewerId = getRequiredIdPart(viewerId, 'viewerId');

  if (creatorId === safeViewerId) {
    throw new Error('Creators cannot Grind their own campaigns.');
  }

  return {
    id: createCampaignGrindId(campaignId, safeViewerId),
    campaignId,
    creatorId,
    viewerId: safeViewerId,
    createdAt: serverTimestamp()
  };
}

export async function createCloudGrind(campaign) {
  assertCloudGrindsEnabled();
  const viewerId = getAuthenticatedViewerId();
  const campaignId = getRequiredIdPart(campaign?.id, 'campaignId');
  const campaignRef = doc(db, CLOUD_COLLECTIONS.campaigns, campaignId);
  const campaignSnapshot = await getDoc(campaignRef);

  if (!campaignSnapshot.exists()) {
    throw new Error('Campaign could not be found.');
  }

  const cloudCampaign = {
    id: campaignSnapshot.id,
    ...campaignSnapshot.data()
  };
  const record = toCloudGrindRecord({ campaign: cloudCampaign, viewerId });
  const grindRef = doc(db, CLOUD_COLLECTIONS.campaignGrinds, record.id);
  const existingGrind = await getDoc(grindRef);

  if (existingGrind.exists()) {
    return {
      alreadyExists: true,
      grind: {
        id: existingGrind.id,
        ...existingGrind.data()
      }
    };
  }

  const { id, ...writeRecord } = record;
  await setDoc(grindRef, writeRecord);
  return {
    alreadyExists: false,
    grind: record
  };
}

export async function hasCloudGrind(campaignId) {
  assertCloudGrindsEnabled();
  const viewerId = getAuthenticatedViewerId();
  const grindId = createCampaignGrindId(
    getRequiredIdPart(campaignId, 'campaignId'),
    viewerId
  );
  const snapshot = await getDoc(doc(db, CLOUD_COLLECTIONS.campaignGrinds, grindId));
  return snapshot.exists();
}

export async function getCloudGrindCount(campaignId) {
  assertCloudGrindsEnabled();
  const campaignGrindsQuery = query(
    collection(db, CLOUD_COLLECTIONS.campaignGrinds),
    where('campaignId', '==', getRequiredIdPart(campaignId, 'campaignId'))
  );
  const snapshot = await getCountFromServer(campaignGrindsQuery);
  return snapshot.data().count || 0;
}

export async function listViewerCloudGrinds() {
  assertCloudGrindsEnabled();
  const viewerId = getAuthenticatedViewerId();
  const viewerGrindsQuery = query(
    collection(db, CLOUD_COLLECTIONS.campaignGrinds),
    where('viewerId', '==', viewerId)
  );
  const snapshot = await getDocs(viewerGrindsQuery);
  return snapshot.docs.map((grindDoc) => ({
    id: grindDoc.id,
    ...grindDoc.data()
  }));
}

export function areCloudGrindsEnabled() {
  return CLOUD_FEATURE_FLAGS.cloudGrindsEnabled;
}
